import { useCallback } from 'react';
import { useGlobalChatState, useInactivityTimer } from './useGlobalChatState';
import { useSendText2API } from './useChatSendApi';

/**
 * Hook for managing hint state and functionality using global state
 * This is the API-based version that replaces the LiveKit-based useHintState
 */
export function useHint(params: { 
  avatarId: string;
  avatarName: string;
  characterPrompt: string;
  userPreferredName: string;
}) {
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();
  const { resetInactivityTimer } = useInactivityTimer();
  const { getHintFromAPI } = useSendText2API(params);
  
  const requestHint = useCallback(async () => {
    // Reset inactivity timer for user interaction
    resetInactivityTimer();
    
    // Call the API to get hints
    await getHintFromAPI();
  }, [resetInactivityTimer, getHintFromAPI]);
  
  const closeHintPanel = useCallback(() => {
    globalChatStateActions.setShowHintPanel(false);
  }, [globalChatStateActions]);
  
  const selectHint = useCallback((hintText: string, onSelect: (text: string) => void) => {
    onSelect(hintText);
    // Don't close panel, allow multiple selections
    // Panel stays open so user can select different hints
  }, []);
  
  const resetHintState = useCallback(() => {
    globalChatStateActions.resetHintState();
  }, [globalChatStateActions]);
  
  return {
    isHintLoading: globalChatStateData.isHintLoading,
    hints: globalChatStateData.hints,
    showHintPanel: globalChatStateData.showHintPanel,
    canGenerateHint: globalChatStateData.canGenerateHint,
    requestHint,
    closeHintPanel,
    selectHint,
    resetHintState
  };
}

